import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { sendPush } from '@/lib/pushNotify';
import { toast } from 'sonner';
import { reviewLikeCountKey } from '@/hooks/useReviewLikeCounts';

export interface UserLike {
  id: string;
  item_type: 'review' | 'artist' | 'concert' | 'tour' | 'list';
  item_id?: string;
  review_id?: string;
  created_at: string;
}

export const userLikesKey = (userId?: string) => ['user-likes', userId ?? 'anon'] as const;

const fetchUserLikes = async (userId: string): Promise<UserLike[]> => {
  const { data, error } = await supabase
    .from('user_likes')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as UserLike[];
};

export const useLikes = (userId?: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use the provided userId or fall back to current user. Each user id has its
  // own cache key, so one member's likes can never leak into another session.
  const targetUserId = userId || user?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: userLikesKey(targetUserId),
    queryFn: () => fetchUserLikes(targetUserId as string),
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Revalidate when the user returns to the app, but only once the cached
    // likes are actually stale — fresh cache is reused and consumers dedupe.
    refetchOnWindowFocus: true,
  });

  const likes = data ?? [];
  // "Resolved" means we actually know this user's likes (cached or fetched).
  const likesResolved = !targetUserId || data !== undefined;

  const setCurrentUserLikes = useCallback(
    (updater: (prev: UserLike[]) => UserLike[]) => {
      if (!user?.id) return;
      queryClient.setQueryData<UserLike[]>(userLikesKey(user.id), (prev) => updater(prev ?? []));
    },
    [queryClient, user?.id]
  );

  const adjustReviewCount = useCallback(
    (reviewId: string, delta: number) => {
      queryClient.setQueryData<number>(reviewLikeCountKey(reviewId), (prev) =>
        prev === undefined ? prev : Math.max(0, prev + delta)
      );
    },
    [queryClient]
  );

  // Ensure a profile row exists for the current user (required by FK on user_likes.user_id)
  const ensureProfileExists = async () => {
    if (!user) return;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile) {
        const username = (user.user_metadata?.username as string) || `user_${user.id.slice(0, 8)}`;
        const name = (user.user_metadata?.name as string) || username;
        await supabase.from('profiles').insert({
          id: user.id,
          username,
          name,
        });
      }
    } catch (e) {
      // Silent fail; insert below may still fail and be logged
      console.warn('ensureProfileExists failed (non-fatal):', e);
    }
  };

  const toggleLike = async (
    itemType: 'artist' | 'concert' | 'tour' | 'review' | 'list',
    itemId: string
  ) => {
    if (!user) {
      toast.error('Please sign in to like items');
      return null;
    }

    const matchColumn = itemType === 'review' ? 'review_id' : 'item_id';

    // If the shared likes cache has not resolved yet (a consumer can tap before
    // the query lands), resolve it through the same query first so we never
    // assume "unliked". Cached data short-circuits with no extra request.
    let previousLikes = queryClient.getQueryData<UserLike[]>(userLikesKey(user.id));
    if (previousLikes === undefined) {
      try {
        previousLikes = await queryClient.ensureQueryData<UserLike[]>({
          queryKey: userLikesKey(user.id),
          queryFn: () => fetchUserLikes(user.id),
          staleTime: 5 * 60 * 1000,
        });
      } catch (err) {
        console.error('Error resolving likes before toggle:', err);
        toast.error('Failed to update like', {
          description: (err as any)?.message || 'Please try again later',
        });
        return null;
      }
    }

    const wasLiked = (previousLikes ?? []).some((like) =>
      itemType === 'review'
        ? like.item_type === 'review' && like.review_id === itemId
        : like.item_type === itemType && like.item_id === itemId
    );

    // Optimistically update the shared cache so every mounted surface reflects
    // the change immediately, not only the card that was tapped.
    if (wasLiked) {
      setCurrentUserLikes((prev) =>
        prev.filter((like) =>
          itemType === 'review'
            ? !(like.item_type === 'review' && like.review_id === itemId)
            : !(like.item_type === itemType && like.item_id === itemId)
        )
      );
      if (itemType === 'review') adjustReviewCount(itemId, -1);
    } else {
      const optimistic: UserLike = {
        id: `optimistic-${itemId}`,
        item_type: itemType,
        review_id: itemType === 'review' ? itemId : undefined,
        item_id: itemType === 'review' ? undefined : itemId,
        created_at: new Date().toISOString(),
      };
      setCurrentUserLikes((prev) => [optimistic, ...prev]);
      if (itemType === 'review') adjustReviewCount(itemId, 1);
    }

    const rollback = () => {
      queryClient.setQueryData<UserLike[]>(userLikesKey(user.id), previousLikes);
      if (itemType === 'review') adjustReviewCount(itemId, wasLiked ? 1 : -1);
    };

    try {
      await ensureProfileExists();

      if (wasLiked) {
        const { error } = await supabase
          .from('user_likes')
          .delete()
          .eq('user_id', user.id)
          .eq('item_type', itemType)
          .eq(matchColumn, itemId);

        if (error) throw error;

        return false;
      }

      // Like
      const insertData: any = {
        user_id: user.id,
        item_type: itemType,
        review_id: itemType === 'review' ? itemId : null,
        item_id: itemType === 'review' ? null : itemId,
      };

      const { data: inserted, error } = await supabase
        .from('user_likes')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Replace the optimistic row with the persisted one.
      setCurrentUserLikes((prev) => [
        inserted as UserLike,
        ...prev.filter((like) => like.id !== `optimistic-${itemId}`),
      ]);

      // Best-effort: create activity (ignore failures)
      try {
        let targetUserId: string | null = null;
        let additionalMetadata: Record<string, any> = {};

        if (itemType === 'review') {
          const { data: reviewData } = await supabase
            .from('reviews')
            .select('user_id, artist_name, tour_name, rating')
            .eq('id', itemId)
            .maybeSingle();
          targetUserId = reviewData?.user_id ?? null;
          additionalMetadata = {
            artist_name: reviewData?.artist_name,
            tour_name: reviewData?.tour_name,
            review_id: itemId,
          };

          // Send like email notification (gated by recipient prefs server-side)
          if (targetUserId && targetUserId !== user.id) {
            const { data: actorProfile } = await supabase
              .from('profiles')
              .select('name, username')
              .eq('id', user.id)
              .maybeSingle();
            supabase.functions.invoke('send-notification-email', {
              body: {
                type: 'like_on_review',
                recipient_user_id: targetUserId,
                actor_user_id: user.id,
                dedupe_key: `like:${user.id}:${itemId}`,
                metadata: {
                  actor_name: actorProfile?.name || actorProfile?.username || 'Someone',
                  actor_username: actorProfile?.username,
                  review_id: itemId,
                  artist_name: reviewData?.artist_name,
                  tour_name: reviewData?.tour_name || reviewData?.artist_name,
                  rating: reviewData?.rating ?? undefined,
                },
              },
            }).catch((err) => console.warn('like email send failed', err));

            // Fire-and-forget push notification via generic send-push
            // 24h cooldown prevents repeat pushes on unlike/re-like.
            sendPush({
              type: 'review_like',
              recipient_user_id: targetUserId,
              actor_user_id: user.id,
              dedupe_key: `review_like:${user.id}:${itemId}`,
              cooldown_seconds: 86400,
              data: { review_id: itemId },
            });
          }
        } else if (itemType === 'list') {
          const { data: listData } = await supabase
            .from('user_lists')
            .select('name, user_id')
            .eq('id', itemId)
            .maybeSingle();
          targetUserId = listData?.user_id ?? null;
          additionalMetadata = { list_name: listData?.name };
          if (targetUserId && targetUserId !== user.id) {
            sendPush({
              type: 'list_like',
              recipient_user_id: targetUserId,
              actor_user_id: user.id,
              body: `Someone liked your list "${listData?.name || 'Untitled'}"`,
              dedupe_key: `list_like:${user.id}:${itemId}`,
              cooldown_seconds: 86400,
              data: { list_id: itemId },
            });
          }
        } else if (itemType === 'artist') {
          const { data: artistData } = await supabase
            .from('artists')
            .select('name')
            .eq('id', itemId)
            .maybeSingle();
          additionalMetadata = { artist_name: artistData?.name };
        } else if (itemType === 'tour') {
          const { data: tourData } = await supabase
            .from('tours')
            .select('name')
            .eq('id', itemId)
            .maybeSingle();
          additionalMetadata = { tour_name: tourData?.name };
        }

        await supabase.from('activities').insert({
          user_id: user.id,
          type: 'like',
          target_id: itemId,
          target_type: itemType,
          metadata: { item_type: itemType, item_id: itemId, target_user_id: targetUserId, ...additionalMetadata },
        });
      } catch (err) {
        console.warn('Failed to track like activity:', err);
      }

      // If liking an artist, create retroactive tour notifications
      if (itemType === 'artist') {
        try {
          const { data: artistForNotif } = await supabase
            .from('artists')
            .select('name')
            .eq('id', itemId)
            .maybeSingle();
          const artistName = artistForNotif?.name || '';

          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
          const { data: recentTours } = await supabase
            .from('tours')
            .select('id, name, year, image')
            .eq('artist_id', itemId)
            .gte('created_at', ninetyDaysAgo.toISOString())
            .limit(10);

          if (recentTours && recentTours.length > 0) {
            const tourActivities = recentTours.map((tour) => ({
              type: 'tour_announced',
              user_id: user.id,
              target_id: user.id,
              target_type: 'tour',
              metadata: {
                artist_name: artistName,
                artist_id: itemId,
                tour_name: tour.name,
                tour_id: tour.id,
                tour_year: tour.year,
                tour_image: tour.image || null,
                retroactive: true,
              },
            }));
            await supabase.from('activities').insert(tourActivities);
          }
        } catch (e) {
          console.warn('Failed to create retroactive tour notifications on like:', e);
        }
      }

      // Fire-and-forget badge check
      supabase.functions.invoke('award-badges').catch(() => {});

      return true;
    } catch (error: any) {
      console.error('Error toggling like:', error);
      rollback();
      toast.error('Failed to update like', {
        description: error?.message || 'Please try again later',
      });
      return null;
    }
  };

  const isLiked = useCallback(
    (itemType: 'artist' | 'concert' | 'tour' | 'review' | 'list', itemId: string) => {
      if (!user) return false;
      return likes.some(
        (like) =>
          (itemType === 'review' && like.item_type === 'review' && like.review_id === itemId) ||
          (itemType !== 'review' && like.item_type === itemType && like.item_id === itemId)
      );
    },
    [likes, user]
  );

  const getLikesByType = useCallback(
    (itemType: 'artist' | 'concert' | 'tour' | 'review' | 'list') =>
      likes.filter((like) => like.item_type === itemType),
    [likes]
  );

  return {
    likes,
    loading: isLoading,
    likesResolved,
    toggleLike,
    isLiked,
    getLikesByType,
    refetch,
  };
};
